import {FC, useEffect, useRef, useState} from 'react'
import {api, ApiError, FlyerData} from '../../services/api'
import {FlyerPoster, FLYER_WIDTH} from '../../components/FlyerPoster'

const MUTED = '#7e8299'

/**
 * Public, shareable live availability flyer (/flyer/live). Standalone — no site
 * chrome, no controls. The poster renders at its fixed design width and is
 * CSS-scaled down to fit narrow screens (so it never breaks layout or scrolls).
 */
const FlyerLivePage: FC = () => {
  const [data, setData] = useState<FlyerData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const posterRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [boxHeight, setBoxHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    api.publicFlyer().then(setData).catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load flyer'))
  }, [])

  useEffect(() => {
    const update = () => {
      const w = wrapRef.current?.clientWidth ?? FLYER_WIDTH
      const s = Math.min(1, w / FLYER_WIDTH)
      setScale(s)
      const h = posterRef.current?.offsetHeight
      if (h) setBoxHeight(h * s)
    }
    update()
    window.addEventListener('resize', update)
    const ro = posterRef.current ? new ResizeObserver(update) : null
    if (ro && posterRef.current) ro.observe(posterRef.current)
    return () => {
      window.removeEventListener('resize', update)
      ro?.disconnect()
    }
  }, [data])

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(160deg, #eef4f0 0%, #f4f6fa 100%)', display: 'flex', justifyContent: 'center'}}>
      {error ? (
        <div style={{color: '#d9214e', fontSize: 15, marginTop: 60}}>{error}</div>
      ) : !data ? (
        <div style={{color: MUTED, fontSize: 15, marginTop: 60}}>Loading availability…</div>
      ) : (
        <div ref={wrapRef} style={{width: '100%', maxWidth: FLYER_WIDTH}}>
          <div style={{height: boxHeight, overflow: 'hidden'}}>
            <div ref={posterRef} style={{width: FLYER_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left'}}>
              <FlyerPoster data={data} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export {FlyerLivePage}
